import {
  Box,
  Card,
  Grid2 as Grid,
  useMediaQuery,
  Typography,
  IconButton,
  TextField,
  Button,
  Autocomplete,
  Tooltip,
  styled,
  tooltipClasses,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import validator from "validator";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useEffect, useState } from "react";
import { useTheme } from "@emotion/react";
import ContractorCard from "../../permitComponents/ContractorCard";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useDispatch, useSelector } from "react-redux";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import RemoveCircleIcon from "@mui/icons-material/RemoveCircle";
import { countiesForLicense } from "../../../data/dbpr";
import { LoadingButton } from "@mui/lab";

import {
  checkLicenseHolderMatch,
  convertIsoToLocalFormat,
} from "../../../functions/helperFunctions";
import LicenseAuto from "./components/LicenseAuto";
import {
  addManagedContractor,
  updateManagedContractor,
} from "../../../store/actions/privateProviderActions";

const PrivateProviderContractors = () => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  //? SCREEN SIZES ##################################################################
  const mobileSize = useMediaQuery(theme.breakpoints.down("sm"));
  const tabletSize = useMediaQuery(theme.breakpoints.down("md"));
  const largeTabletSize = useMediaQuery(theme.breakpoints.down("lg"));
  const largePC = useMediaQuery(theme.breakpoints.up("xl"));
  //? ###############################################################################

  const currentSession = useSelector((state) => state.sessions.currentSession);
  const currentUser = useSelector((state) => state[currentSession].user);

  const [foundContractor, setFoundContractor] = useState(null);
  const [cardPage, setCardPage] = useState(1);
  const [licenseInputError, setLicenseInputError] = useState("");
  const [addContractorError, setAddContractorError] = useState("");
  const [addContractorLoading, setAddContractorLoading] = useState(false);

  const [showLicenseInput, setShowLicenseInput] = useState(false);
  const [showAddInput, setShowAddInput] = useState(false);
  const [addNewActive, setAddNewActive] = useState(false);
  const [licenseeMatch, setLicenseeMatch] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [currentContractor, setCurrentContractor] = useState({});
  const [editLicense, setEditLicense] = useState(false);

  const [managedContractors, setManagedContractors] = useState([]);
  const [totalContractors, setTotalContractors] = useState(0);
  const [contractorPage, setContractorPage] = useState(1);

  useEffect(() => {
    const managedC = currentUser?.managed_contractors;
    if (managedC?.data) {
      setManagedContractors(managedC?.data);
      setTotalContractors(managedC?.total);
      setContractorPage(managedC?.page);

      if (currentContractor && Object?.keys(currentContractor)?.length === 0) {
        setCurrentContractor(managedC?.data?.[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.managed_contractors]);

  const ErrorTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))(({ theme }) => ({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.error.main,
      color: theme.palette.common.white,
      fontWeight: "bold",
    },
    [`& .${tooltipClasses.arrow}`]: {
      color: theme.palette.error.main,
    },
  }));

  useEffect(() => {
    if (currentUser?.user?.expired === true) {
      navigate("/subscription");
    }

    if (currentSession === "subUserSession") {
      navigate("/dashboard");
    }
  }, [currentUser, currentSession, navigate]);

  const handleNavigate = (direction) => {
    let newPage;
    if (direction === "forward") {
      newPage = cardPage + 1;
    } else if (direction === "back") {
      newPage = cardPage - 1;
    }

    setCurrentContractor(managedContractors?.[newPage - 1]);
    contractorFormik.setValues(managedContractors?.[newPage - 1]);
    setCardPage(newPage);
  };

  const handleEditContractor = () => {
    contractorFormik?.setValues(managedContractors?.[cardPage - 1]);
    setAddNewActive(true);
    setFoundContractor(true);
    setShowAddInput(true);
    setEditLicense(true);
    setAddContractorError("");
  };

  const handleAddClick = () => {
    if (editLicense) {
      setAddNewActive(false);
      setFoundContractor(false);
      setShowAddInput(false);
      setEditLicense(false);
    }

    if (showLicenseInput || addNewActive) {
      setShowAddInput(false);
      setFoundContractor(null);
      setAddNewActive(false);
      setShowLicenseInput(false);
      if (managedContractors?.length > 0) {
        contractorFormik?.setValues(managedContractors?.[cardPage - 1]);
      } else {
        contractorFormik.resetForm();
      }
    }
    // else if (addNewActive) {
    //   setAddNewActive(false);
    // }
    else {
      contractorFormik.resetForm();
      setAddNewActive(true);
      setShowLicenseInput(true);
    }
  };

  const handleUpdateLicense = async () => {
    try {
      setFetching(true);
      const resp = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/data/sync-latest-license`,
        {
          license_number:
            managedContractors?.length > 0
              ? managedContractors?.[cardPage - 1]?.licenseNumber
              : null,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );
      contractorFormik?.setValues(resp.data.data);
      // contractorFormik?.setFieldValue("last_sync", resp?.data?.data);
      setCurrentContractor(resp.data.data);
      await dispatch(updateManagedContractor(resp.data.data));
    } catch (error) {
    } finally {
      setFetching(false);
    }
  };

  // CONTRACTOR FORMIK ///
  const handlePhoneChange = (event) => {
    const { value } = event.target;

    if (value.length > 12) {
      return;
    }
    const digitsOnly = value.replace(/\D/g, ""); // Remove non-numeric characters
    let formattedPhoneNumber = "";

    // Add dashes after 3rd and 6th digits
    for (let i = 0; i < digitsOnly.length; i++) {
      if (i === 3 || i === 6) {
        formattedPhoneNumber += "-";
      }
      formattedPhoneNumber += digitsOnly[i];
    }

    // setFormattedPhone(formattedPhoneNumber); // Update the formatted phone in state
    contractorFormik.setValues((oldValue) => ({
      ...oldValue,
      mobile: formattedPhoneNumber,
    })); // Update formik field value
  };

  // CBC022139
  // CBC027113

  // CONTRACTOR FORMIK //
  const contractorFormik = useFormik({
    enableReinitialize: true,
    initialValues: {
      id: "",
      firstName: "",
      lastName: "",
      businessName: "",
      licenseType: "",
      licenseNumber: "",
      rank: "",
      status: "",
      expires: "",
      mainAddress: "",
      mainAddressCounty: "",
      address1: "",
      address2: "",
      address3: "",
      city: "",
      state: "",
      zip: "",
      email: "",
      mobile: "",
    },
    validationSchema: Yup.object({
      firstName: Yup.string()
        .min(2, "First name is too short")
        .required("First name is required"),
      // middleName: Yup.string(),
      lastName: Yup.string()
        .min(2, "Last name is too short")
        .required("Last name is required"),
      businessName: Yup.string()
        .min(2, "Business name is too short")
        .required("Business name is required"),
      mainAddress: Yup.string()
        .min(5, "Main address is too short")
        .required("Main address is required"),
      address1: Yup.string()
        .min(5, "Address Line 1 is too short")
        .required("Address Line 1 is required"),
      city: Yup.string()
        .min(3, "City is too short")
        .required("City is required"),
      state: Yup.string()
        .min(2, "State is too short")
        .required("State is required"),
      zip: Yup.string()
        .min(5, "Zip Code is too short")
        .required("Zip Code is required"),
      mainAddressCounty: Yup.string()
        .min(3, "County is too short")
        .required("County is required"),
      email: Yup.string()
        .email("Must be a valid email")
        .required("Email address is required"),
      mobile: Yup.string()
        .required("Phone number is required")
        .test("PhoneTest", "Phone number is invalid", function (value) {
          const isValidPhoneNumber = validator.isMobilePhone(value);

          if (isValidPhoneNumber) {
            return true;
          } else {
            return false;
          }
        }),
    }),

    onSubmit: async (values, helpers) => {
      try {
        setAddContractorLoading(true);
        const resp = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/privateProvider/contractors/add`,
          {
            values,
            currentSession: currentSession,
            editLicense: editLicense,
          },
          { withCredentials: true }
        );

        if (resp.data.success) {
          if (editLicense) {
            await dispatch(updateManagedContractor(values));
            setEditLicense(false);
          } else {
            await dispatch(addManagedContractor(resp.data.data));
            setCardPage(managedContractors.length + 1);
          }
          setShowAddInput(false);
          setFoundContractor(null);
          setAddNewActive(false);
          setShowLicenseInput(false);
          contractorFormik?.setValues(resp.data.data);
          setCurrentContractor(values);
        }
      } catch (error) {
        setAddContractorError(error?.response?.data?.message);
      } finally {
        setAddContractorLoading(false);
        setTimeout(() => {
          setAddContractorError("");
        }, 5000);
      }
    },
  });

  useEffect(() => {
    // Update the mainAddress based on the individual address fields
    contractorFormik.setFieldValue(
      "mainAddress",
      `${contractorFormik?.values?.address1 || ""} ${
        contractorFormik?.values?.address2 || ""
      } ${contractorFormik?.values?.address3 || ""} ${
        contractorFormik?.values?.city || ""
      }, ${contractorFormik?.values?.state || ""} ${
        contractorFormik?.values?.zip || ""
      }`
        .replace(/\s+/g, " ") // Clean up extra spaces
        .trim()
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    contractorFormik?.values?.address1,
    contractorFormik?.values?.address2,
    contractorFormik?.values?.address3,
    contractorFormik?.values?.city,
    contractorFormik?.values?.state,
    contractorFormik?.values?.zip,
  ]);

  return (
    <FormikProvider value={contractorFormik}>
      <Box
        sx={{
          mx: largePC ? "auto" : 0,
          maxWidth: "1500px",
          py: 1,
          backgroundColor: "#EEF2F6",
          mb: tabletSize ? "5rem" : undefined,
          mt: 1,
        }}
      >
        <Grid
          container
          sx={{
            px: mobileSize ? 1 : tabletSize ? 2 : 3,
          }}
        >
          <Card
            className="step7"
            py={1}
            mb={1}
            component={Grid}
            sx={{
              width: largeTabletSize ? "90vw" : "75vw",
              maxWidth: "1200px",
              mx: "auto",
            }}
          >
            <Box width="100%" mb={2}>
              <Typography variant="h4" align="center">
                Manage Contractors
              </Typography>
            </Box>
            <Grid
              container
              sx={{
                borderTop: "6px solid #f3f3f3",
                // borderBottom: "6px solid #f3f3f3",
                py: 2,
              }}
            >
              <Grid
                size={{ xs: 6 }}
                px={1}
                sx={{ borderRight: "6px solid #f3f3f3", my: 2 }}
              >
                <Box
                  sx={{
                    visibility: Boolean(addNewActive) ? "hidden" : undefined,
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                  }}
                  mb={2}
                >
                  <IconButton
                    onClick={() => handleNavigate("back")}
                    sx={{
                      visibility:
                        cardPage === 1 || Boolean(addNewActive)
                          ? "hidden"
                          : "visible",
                    }}
                  >
                    <ArrowBackIosNewIcon sx={{ fontSize: "20px" }} />
                  </IconButton>

                  <Typography align="center" variant="h5">
                    {totalContractors === 0
                      ? "0/0"
                      : cardPage + "/" + totalContractors}
                  </Typography>
                  <IconButton
                    onClick={() => handleNavigate("forward")}
                    sx={{
                      visibility:
                        totalContractors === 0 ||
                        cardPage === totalContractors ||
                        Boolean(addNewActive)
                          ? "hidden"
                          : "visible",
                    }}
                  >
                    <ArrowForwardIosIcon sx={{ fontSize: "20px" }} />
                  </IconButton>
                </Box>

                <ContractorCard
                  data={
                    addNewActive ? contractorFormik?.values : currentContractor
                  }
                  // mb={0}
                  colorIndex={cardPage}
                  empty={
                    (Boolean(addNewActive) ||
                      managedContractors.length === 0) &&
                    !Boolean(foundContractor)
                  }
                  showLicenseInput={showLicenseInput}
                  contractorSite={true}
                  handleEditContractor={handleEditContractor}
                  addNewActive={addNewActive}
                  currentContractor={currentContractor}
                />
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  {!addNewActive &&
                  currentContractor &&
                  Object?.keys(currentContractor)?.length > 0 ? (
                    <ErrorTooltip
                      title="Edited data will be overwritten!"
                      arrow
                      PopperProps={{
                        modifiers: [
                          {
                            name: "offset",
                            options: {
                              offset: [0, -18], // Adjust the second value to bring it closer vertically
                            },
                          },
                        ],
                      }}
                    >
                      <Button
                        disabled={addNewActive}
                        onClick={handleUpdateLicense}
                        disableRipple
                        sx={{
                          "&:hover": {
                            backgroundColor: "transparent",
                          },
                        }}
                        endIcon={
                          <RefreshIcon className={fetching ? "rotate" : ""} />
                        }
                      >
                        Sync Latest Data
                      </Button>
                    </ErrorTooltip>
                  ) : (
                    <Button
                      disabled={
                        addNewActive ||
                        (currentContractor &&
                          Object?.keys(currentContractor)?.length === 0) ||
                        !currentContractor
                      }
                      onClick={handleUpdateLicense}
                      disableRipple
                      sx={{
                        "&:hover": {
                          backgroundColor: "transparent",
                        },
                      }}
                      endIcon={
                        <RefreshIcon className={fetching ? "rotate" : ""} />
                      }
                    >
                      Sync Latest Data
                    </Button>
                  )}
                </Box>
                <Box
                  sx={{
                    pt: 0,
                    mt: -1,
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{ cursor: "default" }}
                    align="center"
                    variant="caption"
                  >
                    {contractorFormik?.values?.last_sync
                      ? convertIsoToLocalFormat(
                          contractorFormik?.values?.last_sync,
                          currentUser?.subuser
                            ? currentUser?.subuser?.timezone
                            : currentUser?.user?.personal?.[0]?.timeZone ||
                                "America/New_York",
                          "cccc, LLLL dd, yyyy hh:mm a"
                        )
                      : ""}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 6 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    height: "100%",
                    position: "relative",
                  }}
                >
                  <Typography
                    color="primary"
                    className={addNewActive ? "moveUp" : ""}
                    sx={{
                      fontWeight: "bold",
                      position: "absolute",
                      top: addNewActive ? "10px" : "35%",
                      transition: "top 1s, font-size 1s", // Add font-size transition
                      fontSize: editLicense ? "2rem" : "2.25rem", // Corresponding to h4 and h3 sizes
                    }}
                  >
                    {editLicense
                      ? "UPDATE CONTRACTOR"
                      : "Search New Contractor"}
                  </Typography>

                  <IconButton
                    disabled={addContractorLoading}
                    onClick={handleAddClick}
                    sx={{
                      color: "#4caf50", // Green color
                      fontSize: 80, // Increase the size
                      "&:hover": {
                        color: "#388e3c", // Darker green on hover
                      },
                      position: "absolute",
                      top: addNewActive ? "20px" : "47%",
                      // marginTop: showLicenseInput ? "38px" : "0",
                      transition: "top 1s",
                    }}
                    className={
                      addNewActive ? "fadeInBtn" : !showAddInput && "growBtn"
                    }
                  >
                    {addNewActive ? (
                      <RemoveCircleIcon color="error" fontSize="inherit" />
                    ) : (
                      <AddCircleIcon fontSize="inherit" />
                    )}
                  </IconButton>
                  {showAddInput && foundContractor ? (
                    <Box
                      className={addNewActive ? "fadeIn" : "fadeOut"}
                      sx={{ mt: 13, px: 1, transition: "fade 1s" }}
                    >
                      <Grid spacing={1} container>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            disabled
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.firstName &&
                                contractorFormik.errors.firstName
                              )
                            }
                            helperText={
                              contractorFormik.touched.firstName &&
                              contractorFormik.errors.firstName
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="First Name"
                            name="firstName"
                            value={contractorFormik.values.firstName}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                          <TextField
                            disabled
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.lastName &&
                                contractorFormik.errors.lastName
                              )
                            }
                            helperText={
                              contractorFormik.touched.lastName &&
                              contractorFormik.errors.lastName
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="Last Name"
                            name="lastName"
                            value={contractorFormik.values.lastName}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            disabled={editLicense || addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.businessName &&
                                contractorFormik.errors.businessName
                              )
                            }
                            helperText={
                              contractorFormik.touched.businessName &&
                              contractorFormik.errors.businessName
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="Business Name"
                            name="businessName"
                            value={contractorFormik.values.businessName}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.address1 &&
                                contractorFormik.errors.address1
                              )
                            }
                            helperText={
                              contractorFormik.touched.address1 &&
                              contractorFormik.errors.address1
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="Address Line 1"
                            name="address1"
                            value={contractorFormik.values.address1}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.address2 &&
                                contractorFormik.errors.address2
                              )
                            }
                            helperText={
                              contractorFormik.touched.address2 &&
                              contractorFormik.errors.address2
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="Address Line 2"
                            name="address2"
                            value={contractorFormik.values.address2}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.address3 &&
                                contractorFormik.errors.address3
                              )
                            }
                            helperText={
                              contractorFormik.touched.address3 &&
                              contractorFormik.errors.address3
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="Address Line 3"
                            name="address3"
                            value={contractorFormik.values.address3}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>

                        <Grid size={{ xs: 6 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.city &&
                                contractorFormik.errors.city
                              )
                            }
                            helperText={
                              contractorFormik.touched.city &&
                              contractorFormik.errors.city
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="City"
                            name="city"
                            value={contractorFormik.values.city}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.state &&
                                contractorFormik.errors.state
                              )
                            }
                            helperText={
                              contractorFormik.touched.state &&
                              contractorFormik.errors.state
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="State"
                            name="state"
                            value={contractorFormik.values.state}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 3 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.zip &&
                                contractorFormik.errors.zip
                              )
                            }
                            helperText={
                              contractorFormik.touched.zip &&
                              contractorFormik.errors.zip
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="Zip Code"
                            name="zip"
                            value={contractorFormik.values.zip}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Autocomplete
                            disabled={addContractorLoading}
                            sx={{ width: "100%" }}
                            options={countiesForLicense}
                            value={contractorFormik.values.mainAddressCounty} // This should match the selected value
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                size="small"
                                label="County"
                                error={Boolean(
                                  contractorFormik.touched.mainAddressCounty &&
                                    contractorFormik.errors.mainAddressCounty
                                )} // Show error if touched and error exists
                                helperText={
                                  contractorFormik.touched.mainAddressCounty &&
                                  contractorFormik.errors.mainAddressCounty
                                }
                              />
                            )}
                            onChange={(event, newValue) => {
                              // Manually update the form value
                              contractorFormik.setFieldValue(
                                "mainAddressCounty",
                                newValue
                              );
                            }}
                            ListboxProps={{
                              style: {
                                maxHeight: "150px",
                                overflowY: "auto",
                              },
                            }}
                          />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.email &&
                                contractorFormik.errors.email
                              )
                            }
                            helperText={
                              contractorFormik.touched.email &&
                              contractorFormik.errors.email
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={contractorFormik.handleChange}
                            label="Email Address"
                            name="email"
                            value={contractorFormik.values.email}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <TextField
                            disabled={addContractorLoading}
                            size="small"
                            error={
                              !!(
                                contractorFormik.touched.mobile &&
                                contractorFormik.errors.mobile
                              )
                            }
                            helperText={
                              contractorFormik.touched.mobile &&
                              contractorFormik.errors.mobile
                            }
                            onBlur={contractorFormik.handleBlur}
                            onChange={handlePhoneChange}
                            label="Mobile Phone"
                            name="mobile"
                            value={contractorFormik.values.mobile}
                            variant="filled"
                            fullWidth
                          />
                        </Grid>

                        <Grid size={{ xs: 12 }}>
                          <Typography align="center" variant="h6" color="error">
                            {addContractorError}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }} sx={{ width: "100%" }}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <LoadingButton
                              loading={addContractorLoading}
                              color="primary"
                              variant="contained"
                              onClick={() => {
                                setAddContractorError("");
                                contractorFormik.handleSubmit();
                              }}
                            >
                              {editLicense
                                ? "UPDATE CONTRACTOR"
                                : " ADD CONTRACTOR"}
                            </LoadingButton>
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                  ) : (
                    showLicenseInput &&
                    !foundContractor && (
                      <Box sx={{ mt: 0, px: 1, width: "100%" }}>
                        <LicenseAuto
                          setFoundContractor={setFoundContractor}
                          setShowAddInput={setShowAddInput}
                          setShowLicenseInput={setShowLicenseInput}
                        />
                        <Typography
                          align="center"
                          variant="h6"
                          color="error"
                          mb={1}
                          sx={{ minHeight: "1.4rem" }}
                        >
                          {licenseInputError}
                        </Typography>
                      </Box>
                    )
                  )}
                </Box>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Box>
    </FormikProvider>
  );
};

export default PrivateProviderContractors;
